import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Gauge,
  ClipboardList,
  Briefcase,
  Users,
  LogOut,
  Plus,
  Upload,
  Settings as SettingsIcon,
  Sun,
  Moon,
  ChevronDown,
  RefreshCw,
  ShieldCheck,
  ScrollText,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { JackstoneLogo, JackstoneLogoLink } from "@/components/Logo";
import { useRole, ROLE_LABEL } from "@/lib/roles";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: Gauge, testid: "nav-dashboard" },
  { to: "/tests", label: "Pressure Tests", icon: ClipboardList, testid: "nav-tests" },
  { to: "/orders", label: "Orders", icon: Briefcase, testid: "nav-orders" },
  { to: "/employees", label: "Employees", icon: Users, testid: "nav-employees" },
  { to: "/import", label: "Import CSV", icon: Upload, testid: "nav-import", writerOnly: true },
  { to: "/admin/users", label: "User Management", icon: ShieldCheck, testid: "nav-admin-users", adminOnly: true },
  { to: "/admin/audit", label: "Audit Trail", icon: ScrollText, testid: "nav-audit-trail", adminOnly: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", adminOnly: true },
];

export default function Layout() {
  const { user, logout, switchUser } = useAuth();
  const { role, canWrite, isAdmin } = useRole();
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const visibleNav = nav.filter(
    (n) => (!n.writerOnly || canWrite) && (!n.adminOnly || isAdmin)
  );
  const currentNav =
    visibleNav.find((n) => location.pathname.startsWith(n.to)) || visibleNav[0];

  return (
    <div className="min-h-screen flex flex-col xl:flex-row bg-[#f5f1ec]">
      {/* Mobile/tablet top bar — replaces the sidebar on phones & iPads. */}
      <div
        className="xl:hidden bg-[#33424f] text-slate-100 sticky top-0 z-40 border-b border-[#1f2a33]"
        data-testid="mobile-admin-topbar"
      >
        <div className="flex items-center justify-between px-4 py-3 gap-3">
          <JackstoneLogoLink variant="light" className="h-6 w-auto shrink-0" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="mobile-admin-nav-trigger"
                className="flex items-center gap-2 bg-[#262f39] border border-[#1f2a33] px-3 py-2 text-xs subtitle uppercase tracking-[0.2em] text-white min-w-0"
              >
                {currentNav?.icon && <currentNav.icon className="w-4 h-4 shrink-0" />}
                <span className="truncate">{currentNav?.label || "Menu"}</span>
                <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-70" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-testid="mobile-admin-nav-content">
              <DropdownMenuLabel className="text-[10px] subtitle uppercase tracking-[0.25em] text-[#50493b]">
                Control Room
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {visibleNav.map(({ to, label, icon: Icon, testid }) => (
                <DropdownMenuItem
                  key={to}
                  data-testid={`mobile-${testid}`}
                  onClick={() => navigate(to)}
                  className="cursor-pointer text-sm subtitle"
                >
                  <Icon className="w-4 h-4 mr-2 text-[#39678f]" />
                  {label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              {canWrite && (
                <DropdownMenuItem
                  data-testid="mobile-quick-add-test"
                  onClick={() => navigate("/tests/new")}
                  className="cursor-pointer text-sm subtitle text-[#39678f]"
                >
                  <Plus className="w-4 h-4 mr-2" /> New Test
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                data-testid="mobile-dark-mode-toggle"
                onClick={toggleDarkMode}
                className="cursor-pointer text-sm subtitle"
              >
                {darkMode ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                {darkMode ? "Light mode" : "Dark mode"}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="mobile-switch-user"
                onClick={switchUser}
                className="cursor-pointer text-sm subtitle"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Switch user
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="mobile-logout-btn"
                onClick={logout}
                className="cursor-pointer text-sm subtitle text-[#b30035]"
              >
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="px-4 pb-2 text-[10px] subtitle tracking-[0.3em] uppercase text-[#ccb795] truncate">
          {user?.name} · {ROLE_LABEL[role] || role}
        </div>
      </div>

      {/* Desktop sidebar — shown only on wide (>=1280px) screens. */}
      <aside className="hidden xl:flex w-64 bg-[#33424f] text-slate-100 flex-col shrink-0">
        <div className="p-6 border-b border-[#1f2a33]">
          <JackstoneLogoLink variant="light" className="h-7 w-auto" />
          <div className="mt-3 text-[10px] subtitle tracking-[0.3em] uppercase text-[#ccb795]">
            Pressure Test Registry
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {visibleNav.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm subtitle transition-colors border-l-2 ${
                  isActive
                    ? "bg-[#262f39] text-white border-[#39678f]"
                    : "text-slate-400 border-transparent hover:text-white hover:bg-[#262f39]/60"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-[#1f2a33]">
          {canWrite ? (
            <Button
              data-testid="quick-add-test-btn"
              onClick={() => navigate("/tests/new")}
              className="w-full bg-[#39678f] hover:bg-[#2c5272] text-white subtitle uppercase tracking-wider text-xs"
            >
              <Plus className="w-4 h-4 mr-2" /> New Test
            </Button>
          ) : (
            <div className="w-full text-center text-[10px] text-[#8ea0b3] subtitle uppercase tracking-[0.2em] py-2 border border-[#1f2a33]">
              Read-only access
            </div>
          )}
        </div>

        <div className="p-4 border-t border-[#1f2a33] text-xs">
          <div className="text-[#8ea0b3] tracking-[0.2em] uppercase text-[10px] mb-1 subtitle">
            Signed in · {ROLE_LABEL[role] || role}
          </div>
          <div className="font-body font-semibold text-white truncate" data-testid="current-user-name">
            {user?.name}
          </div>
          <div className="text-slate-400 text-[11px] truncate">{user?.email}</div>
          <div className="mt-3 flex items-center gap-3">
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="flex items-center gap-2 text-slate-400 hover:text-[#ccb795] text-xs uppercase tracking-wider subtitle"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
            <button
              data-testid="dark-mode-toggle"
              onClick={toggleDarkMode}
              title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
              className="ml-auto flex items-center gap-1.5 text-slate-400 hover:text-[#ccb795] text-xs uppercase tracking-wider subtitle"
            >
              {darkMode ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
              {darkMode ? "Light" : "Dark"}
            </button>
          </div>
          <button
            data-testid="switch-user-btn"
            onClick={switchUser}
            className="mt-3 flex items-center gap-2 text-slate-400 hover:text-[#ccb795] text-xs uppercase tracking-wider subtitle"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Switch user
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto min-w-0">
        <Outlet />
      </main>
    </div>
  );
}

